import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  GroupedLabelWithValue,
  InputReporting,
  LabelWithValue,
  MarketingPerformanceQuery,
  MarketingPerformanceReportItem,
  Period,
  SalesPerformanceFacetItem,
  SalesPerformanceFacetQuery,
  SalesPerformanceQuery,
  SalesPerformanceReportItem,
  CallsPerformanceReportItem,
  CallsPerformanceQuery,
  CallsPerformanceFacetItem,
  CallsPerformanceFacetQuery,
} from './types';
import { ErrorService } from '../../error';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ReportingApiService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly errorService: ErrorService,
  ) {}

  getReportByEntity = (inputReporting: InputReporting): Observable<any[]> => {
    let parameters: string[];
    parameters = inputReporting.parameters;

    let params = new HttpParams();
    inputReporting.query.forEach((value, key) => {
      params = params.append(key, value);
    });

    let url = this.formatUrl(parameters);

    // console.log(url)

    return this.httpClient.get<LabelWithValue[]>(url, { params: params }).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  getPeriods = (fromDate: Date): Observable<Period[]> => {
    let parameters: string[];
    parameters = ['periods'];
    let url = this.formatUrl(parameters);

    let queryParams = new HttpParams();
    queryParams = queryParams.append('fromDate', fromDate.toDateString());

    return this.httpClient.get<Period[]>(url, { params: queryParams }).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  getReportKpi = (inputReporting: InputReporting): Observable<number> => {
    let parameters: string[];
    parameters = inputReporting.parameters;

    let params = new HttpParams();
    inputReporting.query.forEach((value, key) => {
      params = params.append(key, value);
    });

    let url = this.formatUrl(parameters);

    return this.httpClient.get<number>(url, { params: params }).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  getReportWithGroupedLabelWithValue = (
    inputReporting: InputReporting,
  ): Observable<any[]> => {
    let parameters: string[];
    parameters = inputReporting.parameters;

    let params = new HttpParams();
    inputReporting.query.forEach((value, key) => {
      params = params.append(key, value);
    });

    let url = this.formatUrl(parameters);

    // console.log(url)
    if (!inputReporting.filters) inputReporting.filters = [];

    return this.httpClient
      .post<
        GroupedLabelWithValue[]
      >(url, inputReporting.filters, { params: params })
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  getSalesPerformance = (
    query: SalesPerformanceQuery,
  ): Observable<SalesPerformanceReportItem[]> => {
    const url = this.formatUrl(['salesPerformance']);

    const params = new HttpParams().append('filter', query.filter);

    return this.httpClient
      .get<SalesPerformanceReportItem[]>(url, { params })
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  getMarketingPerformance = (
    query: MarketingPerformanceQuery,
  ): Observable<MarketingPerformanceReportItem[]> => {
    const url = this.formatUrl(['marketingPerformance']);

    const params = new HttpParams().append('filter', query.filter);

    return this.httpClient
      .get<MarketingPerformanceReportItem[]>(url, { params })
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  getCallsPerformance = (
    query: CallsPerformanceQuery,
  ): Observable<CallsPerformanceReportItem[]> => {
    const url = this.formatUrl(['callsPerformance']);

    const params = this.toCallsPerformanceParams(query);

    return this.httpClient
      .get<CallsPerformanceReportItem[]>(url, { params })
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  getCallsPerformanceFacetItems = (
    query: CallsPerformanceFacetQuery,
  ): Observable<CallsPerformanceFacetItem[]> => {
    const url = this.formatUrl(['callsPerformance', 'facets']);

    const params = this.toCallsPerformanceParams(query).append(
      'dimension',
      query.dimension,
    );

    return this.httpClient
      .get<CallsPerformanceFacetItem[]>(url, { params })
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  getSalesPerformanceFacetItems = (
    query: SalesPerformanceFacetQuery,
  ): Observable<SalesPerformanceFacetItem[]> => {
    const url = this.formatUrl(['salesPerformance', 'facets']);

    const params = new HttpParams()
      .append('dimension', query.dimension)
      .append('filter', query.filter);

    return this.httpClient
      .get<SalesPerformanceFacetItem[]>(url, { params })
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  formatUrl = (parameters: string[]): string => {
    let url = environment.comparanooapiurl;
    url += environment.features.reporting.url;

    parameters.forEach((parameters) => {
      url += '/';
      url += parameters;
    });
    return url;
  };

  /**
   * Query string of the calls dashboard selection, shared by the rows and the dropdowns. One repeated
   * key per selected value; an empty list sends no key (= all) and a null id goes as an empty value
   * (`salesTeamIds=`), which the back binds to null.
   */
  private toCallsPerformanceParams(query: CallsPerformanceQuery): HttpParams {
    let params = new HttpParams()
      .append('startDate', query.startDate)
      .append('endDate', query.endDate);
    query.directions.forEach((id) => (params = params.append('directions', id)));
    query.salesTeamIds.forEach(
      (id) => (params = params.append('salesTeamIds', id ?? '')),
    );
    query.salesPersonIds.forEach(
      (id) => (params = params.append('salesPersonIds', id ?? '')),
    );
    return params;
  }
}
